const API_BASE = 'https://api.traktag.com/group-quiz/api';
const WS_BASE = 'wss://api.traktag.com/group-quiz/ws';

class ApiClient {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, { ...options, headers });
    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401 && this.refreshToken) {
        const refreshed = await this.refresh();
        if (refreshed) {
          headers['Authorization'] = `Bearer ${this.accessToken}`;
          const retryResponse = await fetch(url, { ...options, headers });
          return retryResponse.json();
        }
      }
      throw new Error(data.error?.message || 'Request failed');
    }

    return data;
  }

  async refresh() {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken })
      });
      const data = await response.json();
      if (data.success) {
        this.setTokens(data.accessToken, data.refreshToken);
        return true;
      }
    } catch (e) {}
    this.clearTokens();
    return false;
  }

  // Auth
  async register(email, password, displayName) {
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName })
    });
    if (data.success) {
      this.setTokens(data.accessToken, data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  }

  async login(email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    if (data.success) {
      this.setTokens(data.accessToken, data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  }

  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: this.refreshToken })
      });
    } catch (e) {}
    this.clearTokens();
  }

  // User
  async getMe() {
    return this.request('/users/me');
  }

  // Packs
  async getCategories() {
    return this.request('/packs/categories');
  }

  async getPacks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/packs${query ? '?' + query : ''}`);
  }

  async getMyPacks() {
    return this.request('/packs/creator/mine');
  }

  async createPack(packData) {
    return this.request('/packs', {
      method: 'POST',
      body: JSON.stringify(packData)
    });
  }

  async addQuestion(packId, questionData) {
    return this.request(`/packs/${packId}/questions`, {
      method: 'POST',
      body: JSON.stringify(questionData)
    });
  }

  // Sessions
  async createSession(sessionData) {
    return this.request('/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  async joinSession(joinCode, playerName, avatarEmoji) {
    return this.request('/sessions/join', {
      method: 'POST',
      body: JSON.stringify({ joinCode, playerName, avatarEmoji })
    });
  }

  async getSession(sessionId) {
    return this.request(`/sessions/${sessionId}`);
  }

  async startSession(sessionId) {
    return this.request(`/sessions/${sessionId}/start`, { method: 'POST' });
  }

  async nextQuestion(sessionId, questionIndex) {
    return this.request(`/sessions/${sessionId}/next`, {
      method: 'POST',
      body: JSON.stringify({ questionIndex })
    });
  }

  async endSession(sessionId) {
    return this.request(`/sessions/${sessionId}/end`, { method: 'POST' });
  }

  async getLeaderboard(sessionId) {
    return this.request(`/sessions/${sessionId}/leaderboard`);
  }

  // WebSocket
  connectWebSocket(sessionId, token, role = 'player') {
    const params = new URLSearchParams({ sessionId, role });
    if (role === 'host') {
      params.set('token', this.accessToken);
    } else {
      params.set('sessionToken', token);
    }
    return new WebSocket(`${WS_BASE}?${params}`);
  }
}

export const api = new ApiClient();
export default api;
